import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "../../Styles/LocalLeadersForm.css";
import { fileAPI, localLeadersAPI } from "../../api/index"; // adjust path to match your project

// Mandal -> village list, shared with the table. Keep names EXACTLY as your
// backend's `mandals`/`villages` tables expect them, since resolve_geography()
// matches on these strings.
export const MANDAL_VILLAGES = {
  Kandukur: [
    "Anandapuram", "Anantha Sagaram", "Donda Padu", "G.Meka Padu",
    "Jillelamudi", "Kancharagunta", "Kondamudusu Palem", "Kandukur",
    "Kovur", "Machavaram", "Madanagopalapuram", "Mahadevapuram",
    "Mopadu", "Muppalakesaram", "Ogur", "Palukur", "Palur",
    "Pandalapadu", "Vikkiralapeta",
  ],
  Lingasamudram: [
    "Anneboinapalle", "Cheemalapenta", "Chinapavani", "Gangapalem",
    "Jagamreddi Khandrika", "Lingasamudram", "Malakondarayunipalem",
    "Mogilicherla", "Mukteswaram", "Mutyalapadu", "Narasimhapuram",
    "Pentrala", "Racheruvurajupalem", "Rallapadu", "Thimmareddypalem",
    "Thunugunta", "Thurpu Rajupalem", "Veeraraghavunikota",
    "Vengalapuram", "Viswanadhapuram",
  ],
  Gudluru: [
    "Ammavaripalem", "Basireddypalem", "Chevuru", "Chinalatrapi",
    "Dappalampadu", "Darakanipadu", "Gudluru", "Gundlapalem",
    "Kothapeta", "Mocherla", "Nayudupalem", "Parakondapadu Agraharam",
    "Parakondapadu", "Potluru", "Puretipalle", "Ravur",
    "Swarnajipuram", "Venkampeta",
  ],
  Ulavapadu: [
    "Atmakur", "Baddepudi", "Bheemavaram", "Chagallu", "Chaki Cherla",
    "K. Rajupalem", "Karedu", "Kollurupadu", "Krishnapuram",
    "Manneti Kota", "Ramayapatnam", "Veerepalle",
  ],
  "Valletivari Palem": [
    "Ayyavaripalle", "Chundi", "East Polineni Palem", "Kakutur",
    "Kalavalla", "Kondareddipalem", "Kondasamudram", "Naladalapur",
    "Nawabpalem", "Nekunam Puram K.Kandrika", "Nekunampuram @ Pokur",
    "Nukavaram", "Polineni Cheruvu", "Ramachandrapuram",
    "Ramalingapuram", "Sakhavaram", "Sameerapalem", "Singamnenipalle",
    "Veeranna Palem", "Voletivaripalem", "Z. Uppalapadu",
  ],
};

const LocalLeadersForm = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    alias: "",
    position: "",
    party: "",
    voterId: "",
    aadhaar: "",
    mobile: "",
    village: "",
    mandal: "",
    dateJoined: "",
    status: "",
    remarks: "",
  });
  const [photo, setPhoto] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "photo") {
      setPhoto(files[0] || null);
      return;
    }

    if (name === "mandal") {
      // village options depend on the chosen mandal, so reset the village
      // whenever the mandal changes
      const firstVillage = MANDAL_VILLAGES[value]?.[0] || "";
      setFormData((prev) => ({ ...prev, mandal: value, village: firstVillage }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (
      !formData.name ||
      !formData.position ||
      !formData.party ||
      !formData.voterId ||
      !formData.aadhaar ||
      formData.aadhaar.length !== 12 ||
      !formData.mobile ||
      !formData.village ||
      !formData.mandal ||
      formData.mandal === "Select Mandal" ||
      !formData.dateJoined ||
      !formData.status ||
      formData.status === "Select Status" ||
      !photo
    ) {
      setError("Please fill all required fields marked with * (Aadhaar must be 12 digits).");
      return;
    }

    setSubmitting(true);
    try {
      const uploadRes = await fileAPI.upload(photo, "local_leaders");
      const photoUrl =
        uploadRes.data?.url ||
        uploadRes.data?.file_url ||
        uploadRes.data?.photo_url;

      if (!photoUrl) {
        throw new Error(
          "Upload succeeded but the response didn't contain a usable URL field."
        );
      }

      await localLeadersAPI.create({
        leader_name: formData.name,
        alias_name: formData.alias || null,
        position: formData.position,
        party: formData.party,
        epic_no: formData.voterId,
        aadhaar_number: formData.aadhaar,
        mobile_number: formData.mobile,
        village_name: formData.village,
        mandal_name: formData.mandal,
        date_joined: formData.dateJoined, // <input type="date"> gives YYYY-MM-DD
        status: formData.status.toLowerCase(),
        remarks: formData.remarks || null,
        photo_url: photoUrl,
      });

      navigate("/dashboard/LocalLeadersTable");
    } catch (err) {
      console.error("Local leader save failed:", err);
      setError(
        err.response?.data?.detail || err.message || "Something went wrong while saving the leader."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="ldrform-container">
      <div className="ldrform-card">
        <div className="ldrform-header">
          <h2>Add New Local Political Leader</h2>
          <p>Register a local political leader in your constituency</p>
          <button
            className="ldrform-back-btn"
            onClick={() => navigate(-1)}
          >
            Back
          </button>
        </div>

        {error && <div className="ldrform-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="ldrform-grid">

            <div className="ldrform-group">
              <label>Leader Name *</label>
              <input
                type="text"
                name="name"
                placeholder="Enter Name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="ldrform-group">
              <label>Alias / Known As</label>
              <input
                type="text"
                name="alias"
                placeholder="Ex: Ramesh Garu"
                value={formData.alias}
                onChange={handleChange}
              />
            </div>

            <div className="ldrform-group">
              <label>Position *</label>
              <input
                type="text"
                name="position"
                placeholder="Ex: Mandal President"
                value={formData.position}
                onChange={handleChange}
                required
              />
            </div>

            <div className="ldrform-group">
              <label>Party *</label>
              <input
                type="text"
                name="party"
                placeholder="Enter Party Name"
                value={formData.party}
                onChange={handleChange}
                required
              />
            </div>

            <div className="ldrform-group">
              <label>Voter ID *</label>
              <input
                type="text"
                name="voterId"
                placeholder="Enter Voter ID (EPIC No.)"
                value={formData.voterId}
                onChange={handleChange}
                required
              />
            </div>

            <div className="ldrform-group">
              <label>Aadhaar Number *</label>
              <input
                type="text"
                name="aadhaar"
                placeholder="Enter 12-digit Aadhaar Number"
                maxLength="12"
                value={formData.aadhaar}
                onChange={handleChange}
                required
              />
            </div>

            <div className="ldrform-group">
              <label>Mobile Number *</label>
              <input
                type="tel"
                name="mobile"
                placeholder="Enter Mobile Number"
                maxLength="10"
                value={formData.mobile}
                onChange={handleChange}
                required
              />
            </div>

            <div className="ldrform-group">
              <label>Mandal *</label>
              <select name="mandal" value={formData.mandal} onChange={handleChange} required>
                <option value="">Select Mandal</option>
                {Object.keys(MANDAL_VILLAGES).map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>

            <div className="ldrform-group">
              <label>Village *</label>
              <select
                name="village"
                value={formData.village}
                onChange={handleChange}
                required
                disabled={!formData.mandal}
              >
                <option value="">
                  {formData.mandal ? "Select Village" : "Select Mandal first"}
                </option>
                {(MANDAL_VILLAGES[formData.mandal] || []).map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </div>

            <div className="ldrform-group">
              <label>Date Joined *</label>
              <input
                type="date"
                name="dateJoined"
                value={formData.dateJoined}
                onChange={handleChange}
                required
              />
            </div>

            <div className="ldrform-group">
              <label>Status *</label>
              <select name="status" value={formData.status} onChange={handleChange} required>
                <option value="">Select Status</option>
                <option>Active</option>
                <option>Inactive</option>
              </select>
            </div>

            <div className="ldrform-group">
              <label>Upload Photo *</label>
              <input
                type="file"
                name="photo"
                accept="image/*"
                onChange={handleChange}
                required
              />
            </div>

          </div>

          <div className="ldrform-group ldrform-full-width">
            <label>Remarks</label>
            <textarea
              rows="4"
              name="remarks"
              placeholder="Enter Remarks"
              value={formData.remarks}
              onChange={handleChange}
            ></textarea>
          </div>

          <div className="ldrform-btn-group">
            <button type="submit" className="ldrform-submit-btn" disabled={submitting}>
              {submitting ? "Submitting..." : "Submit"}
            </button>
            <button
              type="button"
              className="ldrform-reset-btn"
              onClick={() => {
                setFormData({
                  name: "",
                  alias: "",
                  position: "",
                  party: "",
                  voterId: "",
                  aadhaar: "",
                  mobile: "",
                  village: "",
                  mandal: "",
                  dateJoined: "",
                  status: "",
                  remarks: "",
                });
                setPhoto(null);
                setError("");
              }}
            >
              Reset
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LocalLeadersForm;